import { NgOptimizedImage } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { environment } from '../../../../environments/environment';
import { ProductFiltersButton } from '../../../components/product/product-filters-button/product-filters-button';
import {
  ProductFilters,
  ProductFiltersPanel,
} from '../../../components/product/product-filters-panel/product-filters-panel';
import { ProductList } from '../../../components/product/product-list/product-list';
import { ProductOrderBySelect } from '../../../components/product/product-order-by-select/product-order-by-select';
import { RecentlyViewedProductList } from '../../../components/product/recently-viewed-product-list/recently-viewed-product-list';
import { ShowleftDirective } from '../../../directives/animations/showleft.directive';
import {
  Product,
  ProductCollection,
} from '../../../models/product';
import { FileurlPipe } from '../../../pipes/fileurl.pipe';
import { FileApiService } from '../../../services/apis/file-api.service';
import {
  ProductApiService,
  ProductOrderBy,
} from '../../../services/apis/product-api.service';
import { ApiException, ApiStatus } from '../../../services/apis/status';
import { MessageService } from '../../../services/message.service';
import { TitleService } from '../../../services/title.service';
import { mergeRouter } from '../../../utils/router';
@Component({
  selector: 'app-product-collection-page',
  imports: [
    FileurlPipe,
    NgOptimizedImage,
    RouterModule,
    MatProgressSpinnerModule,
    ProductFiltersPanel,
    FormsModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    ProductOrderBySelect,
    ProductFiltersButton,
    RecentlyViewedProductList,
    NzEmptyModule,
    ProductList,
    ShowleftDirective,
  ],
  templateUrl: './product-collection-page.html',
  styleUrl: './product-collection-page.scss',
})
export class ProductCollectionPage implements OnInit {
  filters: ProductFilters = {
    sizes: [],
    color: '',
    category_ids: [],
    tags: [],
  };
  collection?: ProductCollection;
  collectionLoading: boolean = false;
  productsLoading: boolean = false;
  page: number = 1;
  pageSize: number = 24;
  total: number = 0;
  products: Product[] = [];
  orderBy: ProductOrderBy = ProductOrderBy.RECOMMENDED;
  constructor(
    private titleService: TitleService,
    private productApi: ProductApiService,
    private fileApi: FileApiService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      this.filters.sizes = params.getAll('sizes');
      this.filters.color = params.get('color') ?? '';
      this.filters.category_ids = params
        .getAll('category_ids')
        .map((v) => parseInt(v));
      this.page = parseInt(params.get('page') ?? '1');
      this.orderBy =
        (params.get('order_by') as ProductOrderBy) ??
        ProductOrderBy.RECOMMENDED;
      this.findProductCollectionProducts();
    });
    this.route.paramMap.subscribe((params) => {
      const no = params.get('no') ?? '';
      this.page = 1;
      this.getProductCollection(no);
    });
  }

  get isNewArrivals(): boolean {
    return this.collection?.no === environment.collection.new_arrivals;
  }

  onFiltersChanged() {
    this.page = 1;
    mergeRouter(this.router, this.route, {
      sizes: this.filters.sizes,
      color: this.filters.color,
      category_ids: this.filters.category_ids,
      page: this.page,
    });
    // this.findProductCollectionProducts();
  }

  onOrderByChanged() {
    this.page = 1;
    mergeRouter(this.router, this.route, {
      order_by: this.orderBy,
      page: this.page,
    });
    // this.findProductCollectionProducts();
  }

  async getProductCollection(no: string) {
    try {
      this.collectionLoading = true;
      this.collection = await this.productApi.getProductCollection(no);
      this.setCollectionSeo();
    } catch (error) {
      if (error instanceof ApiException) {
        if (error.status === ApiStatus.PRODUCT_COLLECTION_NOT_FOUND) {
          this.messageService.open('Collection not found', 'OK');
        }
      }
    } finally {
      this.collectionLoading = false;
      this.findProductCollectionProducts();
    }
  }
  async findProductCollectionProducts() {
    if (!this.collection) {
      return;
    }
    try {
      this.productsLoading = true;
      const r = await this.productApi.findProductCollectionProducts(
        this.collection.no,
        {
          page: this.page,
          page_size: this.pageSize,
          color: this.filters.color,
          sizes: this.filters.sizes,
          category_ids: this.filters.category_ids,
          order_by: this.orderBy,
        }
      );
      this.products = r.items;
      this.total = r.total;
      this.page = r.page;
      this.pageSize = r.page_size;
      this.setCollectionSeo();
    } catch (error) {
    } finally {
      this.productsLoading = false;
    }
  }

  onPageChanged(event: PageEvent) {
    this.page = event.pageIndex + 1;
    mergeRouter(this.router, this.route, {
      page: this.page,
    });
    // this.findProductCollectionProducts();
  }

  private setCollectionSeo() {
    if (!this.collection) {
      return;
    }

    const path = `/collection/${this.collection.no}`;
    const description = this.truncate(
      this.collection.description ||
      `Explore ${this.collection.name} at ${environment.appname}, featuring curated women's activewear, yoga clothes, leggings, sports bras, and workout sets.`,
      160
    );
    const image = this.collection.cover
      ? this.fileApi.fileurl(this.collection.cover)
      : '/images/home/veyora-fit-hero.webp';
    const itemList = this.products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: this.titleService.absoluteUrl(`/product/${product.no}`),
      name: product.name,
      image: product.images[0]
        ? this.fileApi.fileurl(product.images[0])
        : undefined,
    }));

    this.titleService.setPageMeta({
      title: this.collection.name,
      canonicalPath: path,
      description,
      image,
      imageAlt: `${this.collection.name} collection by ${environment.appname}`,
      robots: this.hasCollectionQuery() ? 'noindex, follow' : 'index, follow',
      keywords: [
        this.collection.name,
        "women's activewear",
        'yoga clothes',
        'workout clothes',
        'sports bras',
        'leggings',
        'workout sets',
        'athleisure',
        ...this.products.slice(0, 8).map((product) => product.name),
      ],
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: this.collection.name,
          description,
          image: this.titleService.absoluteUrl(image),
          url: this.titleService.absoluteUrl(path),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: environment.host,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: this.collection.name,
              item: this.titleService.absoluteUrl(path),
            },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: itemList,
        },
      ],
    });
  }

  private hasCollectionQuery() {
    return (
      this.page > 1 ||
      this.filters.color.length > 0 ||
      this.filters.sizes.length > 0 ||
      this.filters.category_ids.length > 0 ||
      this.orderBy !== ProductOrderBy.RECOMMENDED
    );
  }

  private truncate(value: string, maxLength: number) {
    if (value.length <= maxLength) {
      return value;
    }

    return `${value.slice(0, maxLength - 3).trim()}...`;
  }
}
